import { useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { type ReasonOptions } from "./noMedRecReasonOptions.js";
import {
  Banner,
  BannerList,
  Button,
  Dropdown,
  PopupDialog,
  PopupDialogTitle,
  TextField,
} from "~/components/design-system/index.js";

export function RecordReasonDialog({
  title,
  dropDownLabel,
  bannerTitle,
  onClose,
  onConfirmClick,
  options,
  loading = false,
}: {
  title: string;
  bannerTitle?: string;
  dropDownLabel: string;
  onClose: () => void;
  onConfirmClick: (
    reason: string,
    reasonDescription: string | undefined,
  ) => void;
  options: readonly ReasonOptions[];
  loading?: boolean;
}) {
  const theme = useTheme();

  const { t } = useTranslation();

  const [reason, setReason] = useState("");
  const [reasonDescription, setReasonDescription] = useState<
    string | undefined
  >(undefined);

  const selectedReason = options.find((o) => o.value === reason);

  const defaultDescriptionPrompt = selectedReason?.descriptionRequired
    ? t("pages.formPage.recordReasonDialog.mandatoryDescriptionTitle")
    : t("pages.formPage.recordReasonDialog.optionalDescriptionTitle");

  const descriptionPrompt =
    selectedReason?.descriptionPrompt ?? defaultDescriptionPrompt;

  const hasReason = !!reason;

  const isDescriptionPromptSet = selectedReason?.descriptionRequired
    ? !!reasonDescription
    : true;

  const bothReasonAndDescriptionValid = hasReason && isDescriptionPromptSet;

  return (
    <PopupDialog open={true} onClose={onClose}>
      <PopupDialogTitle titleText={title} closeDialog={onClose} />
      {bannerTitle && (
        <Box sx={{ mb: 2 }}>
          <Banner title={bannerTitle} bannerType={BannerList.WARNING} />
        </Box>
      )}
      <Dropdown
        label={dropDownLabel}
        selectedValue={reason}
        name={"reason"}
        values={options.map((o) => ({ value: o.value, label: o.value }))}
        onChange={(e) => {
          setReason(e);
          setReasonDescription(undefined);
        }}
      />
      {reason && (
        <Box sx={{ mt: 2 }}>
          <TextField
            name={"reasonDescription"}
            label={descriptionPrompt}
            onChange={(e) => {
              setReasonDescription(e);
            }}
            value={reasonDescription || ""}
            useFullWidth={true}
            multiline={true}
            rows="4"
          />
        </Box>
      )}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          marginTop: theme.spacing(3),
        }}
      >
        <Button
          label={"Close"}
          onClick={onClose}
          variant="outlined"
          disabled={loading}
        />
        <Button
          color="primary"
          disabled={!bothReasonAndDescriptionValid || loading}
          label={loading ? "Saving..." : "Confirm"}
          onClick={() => {
            onConfirmClick(reason, reasonDescription);
          }}
        />
      </Box>
    </PopupDialog>
  );
}
